import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { ChevronDown, ArrowRight } from "lucide-react";
import { useMemo, useEffect } from "react";
import { useState } from "react";
import { ContactModal } from "./ContactModal";

// CSS class for the tag glow effect
const tagGlowClass = "filter drop-shadow-[0_0_8px_rgba(255,99,99,0.5)] hover:drop-shadow-[0_0_12px_rgba(255,99,99,0.7)] transition-all duration-300";

export const Hero = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  
  // Mouse position tracking for cursor-follow effect
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);
  
  // Smooth values with spring physics
  const smoothX = useSpring(mouseX, { stiffness: 200, damping: 30 });
  const smoothY = useSpring(mouseY, { stiffness: 200, damping: 30 });
  
  // Transform mouse position to movement with different intensities for parallax
  const centralMoveX = useTransform(smoothX, (value) => value / 40);
  const centralMoveY = useTransform(smoothY, (value) => value / 40);
  
  // Tags move in opposite direction for parallax effect
  const tagMoveX = useTransform(smoothX, (value) => -value / 25);
  const tagMoveY = useTransform(smoothY, (value) => -value / 25);
  
  // Circle moves more intensely for deeper effect
  const circleMoveX = useTransform(smoothX, (value) => value / 20);
  const circleMoveY = useTransform(smoothY, (value) => value / 20);
  
  // Underline moves slightly differently
  const underlineMoveX = useTransform(smoothX, (value) => value / 30);
  const underlineMoveY = useTransform(smoothY, (value) => value / 30);
  
  // Track mouse position
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      // Get mouse position relative to center of screen
      const centerX = window.innerWidth / 2;
      const centerY = window.innerHeight / 2;
      mouseX.set(e.clientX - centerX);
      mouseY.set(e.clientY - centerY);
    };
    
    window.addEventListener("mousemove", handleMouseMove);
    return () => window.removeEventListener("mousemove", handleMouseMove);
  }, [mouseX, mouseY]);

  // Generate random initial rotation degrees for tags
  const randomRotations = useMemo(() => {
    return Array(10).fill(0).map(() => Math.floor(Math.random() * 16) - 8);
  }, []);

  // Animation variants for the floating effect
  const floatingAnimation = (delay: number) => ({
    initial: { 
      opacity: 0 
    },
    animate: { 
      opacity: [ 0.6, 0.7, 0.8, 0.9, 1, 0.9, 0.8, 0.7],
      y: [0, -10, 0, 5, 0],
      x: [0, 5, 0, -5, 0],
      rotate: [0, 3, 0, -3, 0],
      transition: {
        opacity: { 
          delay, 
          repeat: Infinity, 
          duration: 8 + Math.random() * 7,
          ease: "easeInOut" 
        },
        y: { 
          delay, 
          repeat: Infinity, 
          duration: 10 + Math.random() * 5, 
          ease: "easeInOut" 
        },
        x: { 
          delay, 
          repeat: Infinity, 
          duration: 12 + Math.random() * 6, 
          ease: "easeInOut" 
        },
        rotate: { 
          delay, 
          repeat: Infinity, 
          duration: 14 + Math.random() * 4, 
          ease: "easeInOut" 
        }
      }
    }
  });

  return (
    <motion.div 
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="relative min-h-[85vh] md:min-h-screen flex flex-col items-center justify-center text-center px-4 overflow-hidden"
    >
      {/* <motion.div
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.2 }}
        className="mb-8"
      >
        <img src="/logo.svg" alt="Koech Creatives" className="w-32 h-32 mx-auto mb-6" />
      </motion.div>
       */}
      {/* Tag SVGs with random rotations - all now follow cursor */}
      {/* Row 1 - Top */}
      <motion.div
        className="absolute top-[12%] -left-11 md:top-[13%] bottom-[89%] md:left-[12%] z-0"
        style={{ 
          transform: `rotate(${-15 + randomRotations[0]}deg)`,
          x: tagMoveX,
          y: tagMoveY
        }}
        variants={floatingAnimation(0.4)}
        initial="initial"
        animate="animate"
      >
        <img src="/tag-android.svg" alt="" className={`w-64 h-28 md:w-[32rem] md:h-44 ${tagGlowClass}`} />
      </motion.div>
      
      <motion.div
        className="absolute bottom-[59%] left-[60%] md:top-[15%] md:right-[27%] left-25 z-0"
        style={{ 
          transform: `rotate(${12 + randomRotations[1]}deg)`,
          x: tagMoveX,
          y: tagMoveY
        }}
        variants={floatingAnimation(0.6)}
        initial="initial"
        animate="animate"
      >
        <img src="/tag-ui.svg" alt="" className={`w-56 h-24 md:w-[28rem] md:h-40 ${tagGlowClass}`} />
      </motion.div>
      
      {/* Row 2 - Middle top */}
      <motion.div
        className="absolute top-[24%] -left-10 md:top-[25%] md:left-[12%] z-0"
        style={{ 
          transform: `rotate(${5 + randomRotations[2]}deg)`,
          x: tagMoveX,
          y: tagMoveY
        }}
        variants={floatingAnimation(0.65)}
        initial="initial"
        animate="animate"
      >
        <img src="/tag-branding.svg" alt="" className={`w-56 h-24 md:w-[24rem] md:h-40 ${tagGlowClass}`} />
      </motion.div>
      
      <motion.div
        className="absolute top-[18%] -right-1 md:top-[28%] md:right-[15%] z-0"
        style={{ 
          transform: `rotate(${-5 + randomRotations[3]}deg)`,
          x: tagMoveX,
          y: tagMoveY
        }}
        variants={floatingAnimation(0.7)}
        initial="initial"
        animate="animate"
      >
        <img src="/tag-tvos.svg" alt="" className={`w-56 h-24 md:w-[24rem] md:h-40 ${tagGlowClass}`} />
      </motion.div>
      
      {/* Row 3 - Middle bottom */}
      <motion.div
        className="absolute bottom-[30%] -left-10 md:bottom-[42%] md:left-[5%] z-0"
        style={{ 
          transform: `rotate(${-8 + randomRotations[4]}deg)`,
          x: tagMoveX,
          y: tagMoveY
        }}
        variants={floatingAnimation(0.75)}
        initial="initial"
        animate="animate"
      >
        <img src="/tag_ai.svg" alt="" className={`w-64 h-28 md:w-[26rem] md:h-40 ${tagGlowClass}`} />
      </motion.div>
      
      <motion.div
        className="absolute bottom-[50%] -left-[20%] md:bottom-[30%] md:left-[10%] z-0"
        style={{ 
          transform: `rotate(${-8 + randomRotations[4]}deg)`,
          x: tagMoveX,
          y: tagMoveY
        }}
        variants={floatingAnimation(0.75)}
        initial="initial"
        animate="animate"
      >
        <img src="/tag-web.svg" alt="" className={`w-64 h-28 md:w-[26rem] md:h-40 ${tagGlowClass}`} />
      </motion.div>
      

      <motion.div
        className="absolute bottom-[28%] -right-10 md:bottom-[40%] md:right-[5%] z-0"
        style={{ 
          transform: `rotate(${8 + randomRotations[5]}deg)`,
          x: tagMoveX,
          y: tagMoveY
        }}
        variants={floatingAnimation(0.8)}
        initial="initial"
        animate="animate"
      >
        <img src="/tag-ios.svg" alt="" className={`w-64 h-28 md:w-[26rem] md:h-40 ${tagGlowClass}`} />
      </motion.div>
      
      {/* Row 4 - Bottom */}
      <motion.div
        className="absolute bottom-[24%] -left-10 md:bottom-[20%] md:left-[24%] z-0"
        style={{ 
          transform: `rotate(${8 + randomRotations[6]}deg)`,
          x: tagMoveX,
          y: tagMoveY
        }}
        variants={floatingAnimation(0.85)}
        initial="initial"
        animate="animate"
      >
        <img src="/tag-product.svg" alt="" className={`w-64 h-28 md:w-[28rem] md:h-40 ${tagGlowClass}`} />
      </motion.div>
      
      <motion.div
        className="absolute bottom-[20%] -right-10 md:bottom-[25%] md:right-[8%] z-0"
        style={{ 
          transform: `rotate(${-10 + randomRotations[7]}deg)`,
          x: tagMoveX,
          y: tagMoveY
        }}
        variants={floatingAnimation(0.9)}
        initial="initial"
        animate="animate"
      >
        <img src="/tag-design.svg" alt="" className={`w-64 h-28 md:w-[32rem] md:h-44 ${tagGlowClass}`} />
      </motion.div>
      
      <motion.div
        className="absolute top-[60%] bottom-[10%] -right-[42%] -translate-x-1/2 md:bottom-[16%] right-[25%] z-0"
        style={{ 
          transform: `rotate(${-12 + randomRotations[8]}deg)`,
          x: tagMoveX,
          y: tagMoveY
        }}
        variants={floatingAnimation(0.95)}
        initial="initial"
        animate="animate"
      >
        <img src="/tag-windows.svg" alt="" className={`w-64 h-28 md:w-[28rem] md:h-40 ${tagGlowClass}`} />
      </motion.div>
      <motion.div
        className="absolute top-[18%]  -right-38 -translate-x-1/2 md:top-[16%] z-0"
        style={{ 
          transform: `rotate(${12 + randomRotations[9]}deg)`,
          x: tagMoveX,
          y: tagMoveY
        }}
        variants={floatingAnimation(1.0)}
        initial="initial"
        animate="animate"
      >
        <img src="/tag-consulting.svg" alt="" className={`w-64 h-28 md:w-[28rem] md:h-40 ${tagGlowClass}`} />
      </motion.div>
      
      {/* Cursor-following hero content container */}
      <motion.div 
        className="z-10 relative"
        style={{ 
          x: centralMoveX,
          y: centralMoveY
        }}
      >
        <motion.h1
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="text-3xl md:text-7xl font-bold mb-2 md:mb-4 drop-shadow-lg"
        >
          Hi <span className="inline-block animate-bounce">👋</span> We Are
        </motion.h1>
        
        <div className="flex items-center justify-center flex-wrap gap-2 md:gap-4 relative">
          {/* Koech with underline */}
          <div className="relative">
            <motion.h2
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.4 }}
              className="text-4xl md:text-7xl font-bold drop-shadow-lg"
            >
              Koech
            </motion.h2>
            <motion.div
              initial={{ width: 0, opacity: 0 }}
              animate={{ width: "100%", opacity: 1 }}
              transition={{ delay: 0.7, duration: 0.8 }}
              style={{ 
                x: underlineMoveX,
                y: underlineMoveY
              }}
              className="absolute -bottom-4 md:-bottom-6 left-0 w-full"
            >
              <img 
                src="/doodle-underline.svg" 
                alt="" 
                className="h-4 md:h-5 w-full drop-shadow-sm"
              />
            </motion.div>
          </div>
          
          {/* Creatives with circle */}
          <div className="relative">
            <motion.h2
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.4 }}
              className="text-4xl md:text-7xl font-bold drop-shadow-lg"
            >
              Creatives
            </motion.h2>
            <motion.div
              initial={{ opacity: 0, scale: 0.5 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.9, duration: 0.8 }}
              style={{ 
                x: circleMoveX,
                y: circleMoveY
              }}
              className="absolute inset-0 -z-10 flex items-center justify-center"
            >
              <img 
                src="/circle-highlight.svg" 
                alt="" 
                className="w-[220px] h-[220px] md:w-[300px] md:h-[300px] drop-shadow-sm"
              />
            </motion.div>
          </div>
        </div>
        
        <motion.p
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="text-base md:text-xl text-muted-foreground mb-8 md:mb-12 max-w-2xl mt-4 md:mt-8 drop-shadow"
        >
          We create beautiful digital experiences that inspire and connect
        </motion.p>
      </motion.div>
      
      <motion.div
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.6 }}
        style={{ 
          x: centralMoveX,
          y: centralMoveY
        }}
        className="absolute bottom-4 md:bottom-8 z-10"
      >
        <ChevronDown className="w-6 h-6 md:w-8 md:h-8 animate-bounce text-primary" />
      </motion.div>
    </motion.div>
  );
};